import { createClient, type SupabaseClient } from '@supabase/supabase-js'

// Use import.meta.env.* directly so Vite can statically replace vars in bundled workspace deps.
// Guard for non-Vite runtimes (e.g., Node/Vitest) where import.meta.env may be undefined.
const env = (typeof import.meta !== 'undefined' ? import.meta.env || {} : {}) as Record<string, string | undefined>

async function fetchWithRetry(input: RequestInfo | URL, init?: RequestInit, retries = 3): Promise<Response> {
  try {
    return await fetch(input, init)
  } catch (err) {
    if (retries <= 0) throw err
    await new Promise((r) => setTimeout(r, 300 * (4 - retries)))
    return fetchWithRetry(input, init, retries - 1)
  }
}

function createSupabaseClient(): SupabaseClient {
  const supabaseUrl = env.VITE_SUPABASE_URL ?? ''
  const supabaseAnonKey = env.VITE_SUPABASE_ANON_KEY ?? ''
  if (!supabaseUrl || !supabaseAnonKey) {
    throw new Error('Missing required environment variables: VITE_SUPABASE_URL and/or VITE_SUPABASE_ANON_KEY')
  }
  return createClient(supabaseUrl, supabaseAnonKey, {
    global: {
      fetch: (input: RequestInfo | URL, init?: RequestInit) => fetchWithRetry(input, init),
    },
    db: {
      schema: 'public',
    },
  })
}

let cachedClient: SupabaseClient | null = null

function getSupabaseClient(): SupabaseClient {
  if (!cachedClient) {
    cachedClient = createSupabaseClient()
  }
  return cachedClient
}

/**
 * Lazy singleton so the module can be imported in test/CI environments that do not
 * provide VITE_* env vars until the client is actually used.
 *
 * `from` and `rpc` are extra-lazy: they only instantiate the real client when
 * called. This lets `createDbClient(supabase)` be used at import time without
 * requiring environment variables.
 */
export const supabase = new Proxy({} as SupabaseClient, {
  get(_target, prop, _receiver) {
    if (prop === 'from' || prop === 'rpc') {
      return (...args: unknown[]) => {
        const client = getSupabaseClient()
        const method = client[prop as 'from' | 'rpc'] as (...args: unknown[]) => unknown
        return method.apply(client, args)
      }
    }
    const client = getSupabaseClient()
    const value = (client as unknown as Record<string | symbol, unknown>)[prop]
    return typeof value === 'function' ? (value as (...args: unknown[]) => unknown).bind(client) : value
  },
}) as SupabaseClient

/**
 * Set statement_timeout as a second layer of defense.
 * The primary timeout is set at the role level (authenticated = 5s, service_role = 30s)
 * in migration 20260610_phase2_statement_timeout.sql.
 * This client-level setting applies to the current session only.
 */
export async function setClientStatementTimeout(ms: number): Promise<void> {
  const { error } = await supabase.rpc('set_config', {
    setting_name: 'statement_timeout',
    setting_value: String(ms),
    is_local: true,
  })
  if (error) {
    // Non-fatal: role-level timeout is the real guard
    console.warn('setClientStatementTimeout failed:', error.message)
  }
}
